// Livewire focus guard.
//
// Two separate mechanisms put `disabled` on the control a user just activated,
// and either one blurs it. Livewire's own supportDisablingFormsDuringRequest
// disables the submit button (plus selects, checkboxes and radios) of every
// wire:submit form for the duration of the request; separately, the app's many
// hand-written wire:loading.attr="disabled" bindings do the same for wire:click
// buttons. A disabled element is unfocusable, so the browser silently moves
// focus to <body> with no focusin event, and Livewire never restores it - it has
// no focus handling at all. For a keyboard user every save then dumps focus at
// the top of the document, and after a rejected save the only way to find the
// validation error is to tab back down the whole page.
//
// The guard does not care which mechanism fired: it acts on the outcome, focus
// having fallen to <body> during a commit of the component that held it.
//
// This hook watches each component commit. When the commit started with focus
// inside the component and ends with focus on <body>, it moves focus to the
// validation error THIS commit produced (the <p data-input-error> rendered by
// components/input-error.blade.php), or back to the control the user was on when
// the commit produced no error.
//
// Timing constraint: both disable mechanisms undo themselves in respond(), which
// runs before the morph, and the morph itself is deferred behind two microtasks
// (see supportMorphDom in vendor livewire.esm.js). succeed() +
// requestAnimationFrame therefore lands after the morph, when the control is
// focusable again and the error nodes are in the DOM.

// The blur caused by the disable fires no focusin, so track the last element
// that held focus; by commit time document.activeElement may already be <body>.
// focusOutAt dates that blur, which is how we tell a blur this request caused
// from a user deliberately clicking empty space minutes earlier.
let lastFocused = null;
let focusOutAt = 0;

document.addEventListener('focusin', (event) => {
    lastFocused = event.target;
});

document.addEventListener('focusout', () => {
    focusOutAt = performance.now();
});

function focusLost() {
    const active = document.activeElement;

    return active === null || active === document.body || active === document.documentElement;
}

function isVisible(el) {
    if (!el.isConnected) return false;

    if (typeof el.checkVisibility === 'function') {
        return el.checkVisibility({ visibilityProperty: true });
    }

    return el.offsetParent !== null;
}

// The element the guard should act for, or null when this commit's component
// never held focus.
//
// The second branch handles focus already being gone by the time the commit is
// queued. It must not arm on a deliberate click into empty space, which also
// leaves focus on <body>, or an unrelated poll commit would yank focus back to
// whatever the user last used. Two signals say the request caused this blur: the
// element is already disabled, or the blur happened within a few frames of this
// commit. The timing one matters because the order is not guaranteed - under a
// slow response the blur can land before Livewire has applied `disabled`, and
// requiring the attribute alone silently failed to arm exactly there.
const BLUR_CAUSED_BY_REQUEST_MS = 500;

function focusedElementFor(componentEl) {
    if (componentEl.contains(document.activeElement)) {
        return document.activeElement;
    }

    if (!focusLost() || !lastFocused || !componentEl.contains(lastFocused)) {
        return null;
    }

    const blurredByThisRequest = lastFocused.matches(':disabled')
        || (performance.now() - focusOutAt) < BLUR_CAUSED_BY_REQUEST_MS;

    return blurredByThisRequest ? lastFocused : null;
}

// Livewire dehydrates the validation error bag into the snapshot memo on every
// response (SupportValidation::dehydrate) and hydrates it back on the next
// request, so an error outlives the commit that raised it: only a validate()
// that passes clears it. "An error is on screen" therefore says nothing about
// whether THIS commit produced it - the memo does. Without this check, cancelling
// a pending invitation (which validates nothing) threw focus onto the Add User
// error left over from an earlier rejected save.
function errorBag(component) {
    const bag = component.snapshot?.memo?.errors;

    return bag && typeof bag === 'object' ? bag : null;
}

function messagesFor(bag, key) {
    return JSON.stringify(bag?.[key] ?? null);
}

// Only this component's own error nodes. A nested component's errors sit inside
// our root but belong to its snapshot, and the morph skips child component roots,
// so they are never ours to focus.
function ownErrorNodes(componentEl) {
    return [...componentEl.querySelectorAll('[data-input-error]')]
        .filter((node) => node.closest('[wire\\:id]') === componentEl)
        .filter(isVisible);
}

function errorToFocus(componentEl, before, after) {
    const nodes = ownErrorNodes(componentEl);

    if (nodes.length === 0) return null;

    // No usable signal (a Livewire that stops shipping the memo key): restore
    // focus rather than guess which error is new. Degrading to "never steal
    // focus" is the safe direction.
    if (!before || !after) return null;

    // SupportValidation::dehydrate drops any error key that is not a public
    // property (Utils::hasProperty), so those errors are never hydrated back and
    // can only have come from this commit. addError('cleanup') and Jetstream's
    // validateWithBag() keys land here.
    const fresh = nodes.filter((node) => !(node.dataset.inputError in after));

    if (fresh.length > 0) return fresh[0];

    // Otherwise focus the first error whose own messages this commit changed,
    // which is also what keeps a rejected save inside a modal from focusing a
    // stale error further up the page.
    return nodes.find((node) => {
        const key = node.dataset.inputError;

        return messagesFor(after, key) !== messagesFor(before, key);
    }) ?? null;
}

function isRestorable(el) {
    return el.isConnected && isVisible(el) && !el.matches(':disabled');
}

function restoreFocus(componentEl, armed, before, after) {
    if (!focusLost()) return; // the user already moved on; never steal focus

    const error = errorToFocus(componentEl, before, after);

    if (error) {
        error.focus();

        return;
    }

    // Commits can overlap (a wire:model.live sync racing the submit it precedes).
    // Each callback restores the LATEST focused element, never its own capture:
    // the earlier commit's capture is stale by then, and restoring it would put
    // focus back on the control the user had already left. And when that latest
    // element is only ineligible because a still-running commit has it disabled,
    // do nothing rather than fall back: that commit's own callback restores it
    // once the loading state clears.
    if (lastFocused && componentEl.contains(lastFocused)) {
        if (isRestorable(lastFocused)) lastFocused.focus();

        return;
    }

    if (armed && componentEl.contains(armed) && isRestorable(armed)) {
        armed.focus();
    }
}

document.addEventListener('livewire:init', () => {
    if (!window.Livewire) return;

    window.Livewire.hook('commit', ({ component, succeed }) => {
        const armed = focusedElementFor(component.el);

        if (!armed) return;

        // Captured before the response replaces component.snapshot; mergeNewSnapshot
        // runs before succeed(), so the second read is this commit's result. Read
        // it in succeed rather than in the frame, so a second commit landing in
        // between cannot swap it underneath us.
        const before = errorBag(component);

        succeed(() => {
            const after = errorBag(component);

            requestAnimationFrame(() => restoreFocus(component.el, armed, before, after));
        });
    });
});
